// Données sur l'usage des plantes médicinales selon les âges de la vie

export interface AgeCategory {
  id: string;
  label: string;
  ageRange: string;
  description: string;
  allowedForms: string[];
  forbiddenForms: string[];
  safePlants: string[];
  warnings: string[];
  dosageNotes: string[];
}

export const ageCategories: AgeCategory[] = [
  {
    id: 'infant',
    label: 'Nourrisson',
    ageRange: '0 à 3 ans',
    description: 'Usage très restreint. Toujours avec avis médical. Risque de déshydratation et de réactions imprévues.',
    allowedForms: ['Tisanes légères', 'Hydrolats doux'],
    forbiddenForms: ['Huiles essentielles', 'Alcoolatures', 'Teintures mères'],
    safePlants: ['Mauve', 'Oranger', 'Mélisse', 'Fenouil', 'Tilleul', 'Verveine', 'Plantain', 'Coquelicot (à surveiller)'],
    warnings: [
      'Si allaitement : certaines plantes peuvent passer dans le lait → effet indirect sur bébé',
      'Risque de déshydratation important',
      'Réactions imprévues possibles'
    ],
    dosageNotes: ['Doses très faibles uniquement', 'Toujours diluer les préparations']
  },
  {
    id: 'child',
    label: 'Jeune enfant',
    ageRange: '3 à 10 ans',
    description: 'Métabolisme encore immature. Très réactif aux plantes → petites doses uniquement.',
    allowedForms: ['Tisanes (3 cuillères à café par jour)', 'Hydrolats', 'Poudre (pointe de couteau)'],
    forbiddenForms: ['Alcoolatures', 'Huiles essentielles (surtout en interne)'],
    safePlants: [],
    warnings: [
      'Métabolisme encore immature',
      'Très réactif aux plantes → petites doses',
      'La menthe poivrée en huile essentielle peut provoquer convulsions ou spasmes respiratoires',
      'Éviter le Ginkgo Biloba'
    ],
    dosageNotes: [
      'Tisanes : 3 cuillères à café par jour maximum',
      'Poudre : pointe de couteau uniquement'
    ]
  },
  {
    id: 'preteen',
    label: 'Préado / Ado',
    ageRange: '10 à 16 ans',
    description: 'Métabolisme proche de l\'adulte. Adapter la dose au poids.',
    allowedForms: ['Tisanes', 'Hydrolats', 'Gélules (doses adaptées)', 'Extraits fluides (doses réduites)'],
    forbiddenForms: [],
    safePlants: [],
    warnings: [
      'Prudence avec les plantes à effet hormonal en période pubertaire',
      'Adapter la dose au poids corporel'
    ],
    dosageNotes: [
      'Doses à adapter selon le poids',
      'Généralement 50-75% de la dose adulte'
    ]
  },
  {
    id: 'pregnant',
    label: 'Femme enceinte',
    ageRange: 'Grossesse',
    description: 'Usage très limité. Manque d\'études → principe de précaution.',
    allowedForms: ['Tisanes légères', 'Hydrolats doux'],
    forbiddenForms: ['Huiles essentielles', 'Alcoolatures', 'Plantes utérotoniques'],
    safePlants: ['Mauve', 'Oranger', 'Mélisse', 'Tilleul', 'Plantain', 'Ortie', 'Gingembre'],
    warnings: [
      'Risques possibles : Avortement, Prématurité, Malformations, Troubles du développement',
      'Manque d\'études cliniques → principe de précaution',
      'Éviter les plantes à action hormonale'
    ],
    dosageNotes: ['Doses réduites', 'Privilégier les formes douces']
  },
  {
    id: 'breastfeeding',
    label: 'Femme allaitante',
    ageRange: 'Allaitement',
    description: 'Privilégier les plantes autorisées pour nourrisson car elles passent dans le lait.',
    allowedForms: ['Tisanes légères', 'Hydrolats doux'],
    forbiddenForms: ['Huiles essentielles', 'Alcoolatures'],
    safePlants: ['Mauve', 'Oranger', 'Mélisse', 'Tilleul', 'Plantain'],
    warnings: [
      'Les plantes passent dans le lait maternel',
      'Certaines plantes peuvent diminuer la lactation : sauge, menthe, persil, cerfeuil',
      'Certaines plantes peuvent modifier le goût du lait : ail, oignon, valériane'
    ],
    dosageNotes: ['Privilégier les plantes autorisées pour nourrisson']
  },
  {
    id: 'elderly',
    label: 'Personne âgée',
    ageRange: '65 ans et +',
    description: 'Métabolisme ralenti. Moins bonne élimination des principes actifs → risque d\'accumulation et de toxicité.',
    allowedForms: ['Tisanes', 'Hydrolats', 'Gélules (doses réduites)'],
    forbiddenForms: [],
    safePlants: [],
    warnings: [
      'Métabolisme ralenti → accumulation possible',
      'Moins bonne élimination des principes actifs',
      'Risque d\'interactions médicamenteuses accru',
      'Exemple : infusion de thym > 10 min = toxicité accrue',
      'Plantes à tanins (ex : fraisier) peuvent neutraliser certains médicaments (ex : digitaline)'
    ],
    dosageNotes: [
      'Diminuer les doses de 25-50%',
      'Éviter les plantes à toxicité potentielle',
      'Vérifier les interactions médicamenteuses'
    ]
  }
];

// Plantes à éviter selon la catégorie
export const plantsToAvoid: Record<string, string[]> = {
  infant: ['Ginkgo Biloba', 'Millepertuis', 'Ginseng', 'Réglisse'],
  child: ['Ginkgo Biloba', 'Menthe poivrée (HE)', 'Eucalyptus (HE)', 'Romarin à camphre'],
  preteen: ['Gattilier', 'Houblon', 'Sauge sclarée', 'Soja'],
  pregnant: ['Sauge', 'Romarin', 'Thuya', 'Armoise', 'Rue', 'Absinthe', 'Persil (grandes doses)'],
  breastfeeding: ['Sauge', 'Menthe', 'Persil', 'Cerfeuil'],
  elderly: ['Ginkgo (si anticoagulants)', 'Millepertuis (interactions)', 'Réglisse (HTA)']
};

// Formes galéniques recommandées
export const recommendedForms: Record<string, { forms: string[]; notes: string }> = {
  infant: {
    forms: ['Tisanes très légères', 'Hydrolats dilués'],
    notes: 'Usage très restreint, toujours avec avis médical'
  },
  child: {
    forms: ['Tisanes', 'Hydrolats', 'Poudres microdosées'],
    notes: 'Éviter huiles essentielles et alcoolatures'
  },
  preteen: {
    forms: ['Tisanes', 'Hydrolats', 'Gélules adaptées', 'Extraits fluides'],
    notes: 'Adapter les doses au poids corporel'
  },
  pregnant: {
    forms: ['Tisanes légères', 'Hydrolats doux'],
    notes: 'Principe de précaution, consulter un professionnel'
  },
  breastfeeding: {
    forms: ['Tisanes légères', 'Hydrolats'],
    notes: 'Choisir les plantes compatibles avec le nourrisson'
  },
  elderly: {
    forms: ['Tisanes', 'Hydrolats', 'Gélules doses réduites'],
    notes: 'Réduire les doses, surveiller les interactions'
  }
};

// Fonction pour déterminer la catégorie d'âge
export function getAgeCategory(age: number, isPregnant?: boolean, isBreastfeeding?: boolean): AgeCategory | null {
  if (isPregnant) return ageCategories.find(c => c.id === 'pregnant') || null;
  if (isBreastfeeding) return ageCategories.find(c => c.id === 'breastfeeding') || null;
  
  if (age < 3) return ageCategories.find(c => c.id === 'infant') || null;
  if (age < 10) return ageCategories.find(c => c.id === 'child') || null;
  if (age < 16) return ageCategories.find(c => c.id === 'preteen') || null;
  if (age >= 65) return ageCategories.find(c => c.id === 'elderly') || null;
  
  return null; // Adulte standard
}

// Vérifier si une plante est sûre pour une catégorie d'âge
export function isPlantSafeForCategory(plantName: string, categoryId: string): boolean {
  const avoid = plantsToAvoid[categoryId] || [];
  return !avoid.some(p => plantName.toLowerCase().includes(p.toLowerCase()));
}

// Obtenir les avertissements spécifiques pour une catégorie
export function getWarningsForCategory(categoryId: string): string[] {
  const category = ageCategories.find(c => c.id === categoryId);
  return category?.warnings || [];
}

// Informations générales à retenir
export const generalGuidelines = {
  title: 'Plantes médicinales & Âges de la vie',
  introduction: `L'usage des plantes médicinales dépend de l'âge (nourrisson, enfant, adulte, personne âgée) et de l'état physiologique (grossesse, allaitement). Même une plante douce peut être inadaptée selon la personne. Toujours raisonner en précaution et avec un dosage adapté.`,
  keyPoints: [
    'Privilégier les formes douces (tisanes, hydrolats)',
    'Toujours adapter la dose à l\'âge et au métabolisme',
    'Certaines plantes peuvent être puissantes voire dangereuses',
    'En cas de doute : commencer par une faible dose ou s\'abstenir',
    'Continuer à se former : sécurité et efficacité'
  ]
};
